// © 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
// This AWS Content is provided subject to the terms of the AWS Customer Agreement available at
// http://aws.amazon.com/agreement or other written agreement between Customer and either
// Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
import Form from "@awsui/components-react/form";
import FormField from "@awsui/components-react/form-field";
import Input from "@awsui/components-react/input";
import Select from "@awsui/components-react/select";
import Container from "@awsui/components-react/container";
import Header from "@awsui/components-react/header";
import SpaceBetween from "@awsui/components-react/space-between";
import Button from "@awsui/components-react/button";
import Textarea from "@awsui/components-react/textarea";
import Box from "@awsui/components-react/box";
import moment from "moment";
import { DatePicker } from "antd";
import "../../index.css";
import React, { useState, useEffect, useRef } from "react";
import {
  getGroupMemberships,
  requestTeam,
  fetchApprovers,
  fetchOU,
  getSetting,
  getMgmtAccountPs,
  fetchPolicy,
  validateRequest,
  fetchUsers,
} from "../Shared/RequestService";
import { useHistory } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { onPublishPolicy } from "../../graphql/subscriptions";
import params from "../../parameters.json";

// Slack のユーザーグループ。IdC には存在しないのでここで持つ（増やすときはこの配列に追加する）。
// value がそのまま申請理由に埋め込まれる内部表記で、Slack 通知の生成時に
// <!subteam^…> へ変換される（logiless-governance-terraform の team_slack/common.py）
const MENTION_GROUPS = [
  { name: "engineers", token: "@[group:engineers]" },
  { name: "product_enhancement", token: "@[group:product_enhancement]" },
];

// 候補に出さない IdC ユーザー（共有ユーザーと、権限昇格を申請しないメンバー）
const MENTION_EXCLUDED_USERS = ["payment-management", "morichika.higa"];

// Duration の既定値（時間）。上限（eligibility policy の Max duration）は 24 だが、短い申請の方が
// よいので既定は 4 に留め、長くするのは申請者が意図して数字を変えたときだけにする。
// LONG_DURATION_HOURS を超える申請は通知基盤（logiless-governance-terraform の team_slack、
// 環境変数 LONG_REQUEST_HOURS）が承認者に精査を促す。ここは説明文に出すだけなので、両方を同時に変える
const DEFAULT_DURATION_HOURS = 4;
const LONG_DURATION_HOURS = 9;

// 申請の大半が prod 向けなので、アカウントは選択済みの状態で開く。申請者の候補に無ければ空のままにする。
// 権限は既定を置かない。最も強い権限を既定にすると、弱い権限で足りる作業でもそのまま申請されやすくなるため
const DEFAULT_ACCOUNT_NAME = "logiless-prod";

// カーソルの直前にある入力途中の「@なんとか」を拾う。
// 行頭か区切り文字の直後の @ だけを見る（メールアドレス等で誤爆させないため）
const MENTION_TYPING = /(^|[\s(（「])@([\w.-]*)$/;

// カーソル移動を拾うキー。移動に追随しないと、挿入位置が入力時のままずれる
const CARET_KEYS = [
  "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown",
];

function Request(props) {
  const [email, setEmail] = useState("");

  const [item, setItem] = useState([]);

  const [duration, setDuration] = useState(String(DEFAULT_DURATION_HOURS));
  const [durationError, setDurationError] = useState("");

  const [justification, setJustification] = useState("");
  const [justificationError, setJustificationError] = useState("");
  // 申請理由の中にメンションを埋め込むための候補と入力状態。
  // 選ぶと @[user:...] / @[group:...] という内部表記が本文に入り、
  // Slack 通知を作るときに実際のメンションへ変換される
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [mentionStatus, setMentionStatus] = useState("loading");
  // メニューは「@ の位置」で持つ。カーソルを動かしても挿入位置がずれないようにするため
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const justificationRef = useRef(null);
  const activeItemRef = useRef(null);
  const dismissedAt = useRef(-1);
  const mentionOpen = mentionStart >= 0;

  const [role, setRole] = useState([]);
  const [roleError, setRoleError] = useState("");

  const [account, setAccount] = useState([]);
  const [accountError, setAccountError] = useState("");

  const [time, setTime] = useState("");
  const [timeError, setTimeError] = useState("");

  const [ticketNo, setTicketNo] = useState("");
  const [ticketError, setTicketError] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [accountStatus, setAccountStatus] = useState("loading");

  const [permissions, setPermissions] = useState([]);
  const [permissionStatus, setPermissionStatus] = useState("loading");

  const [submitLoading, setSubmitLoading] = useState(false);

  const [mgmtPs, setMgmtPs] = useState([]);

  const [maxDuration, setMaxDuration] = useState(9);
  const [ticketRequired, setTicketRequired] = useState(true);
  const [approvalRequired, setApprovalRequired] = useState(true);

  const history = useHistory();

  function concatenateAccounts(data) {
    let allAccounts = data.map((item) => item.accounts);
    allAccounts = [].concat.apply([], allAccounts);

    let uniqueAccounts = new Set();
    allAccounts.forEach((account) => {
      uniqueAccounts.add(JSON.stringify(account));
    });

    return Array.from(uniqueAccounts).map((account) => JSON.parse(account));
  }

  function concatenatePermissions(data) {
    let uniquePermissions = new Set();
    data.forEach((permission) => {
      uniquePermissions.add(JSON.stringify(permission));
    });

    return Array.from(uniquePermissions).map((permission) =>
      JSON.parse(permission)
    );
  }

  function permissionsFor(policy, accountId) {
    let permissionData = [];
    policy.forEach((data) => {
      data.accounts.forEach((account) => {
        if (account.id == accountId) {
          permissionData = permissionData.concat(data.permissions);
        }
      });
    });
    return concatenatePermissions(permissionData);
  }

  function maxDurationFor(policy, accountId) {
    let max = null;
    policy.forEach((data) => {
      data.accounts.forEach((account) => {
        if (account.id == accountId) {
          max = data.duration;
        }
      });
    });
    return max;
  }

  // アカウントの選択に連動して、権限の候補・時間の上限・時間の既定値を更新する。
  // policy を引数で受けるのは、購読で候補が届いた直後（state の item がまだ古い）にも使うため
  function selectAccount(policy, option) {
    setAccount(option);
    setRole([]);
    setPermissions(permissionsFor(policy, option.value));
    const max = maxDurationFor(policy, option.value);
    if (max !== null) {
      setMaxDuration(max);
    }
    setDuration(
      String(
        Math.min(DEFAULT_DURATION_HOURS, Number(max ?? DEFAULT_DURATION_HOURS))
      )
    );
  }

  function applyDefaults(policy) {
    const defaultAccount = concatenateAccounts(policy).find(
      (account) => account.name === DEFAULT_ACCOUNT_NAME
    );
    if (!defaultAccount) {
      return;
    }
    selectAccount(policy, {
      label: defaultAccount.name,
      value: defaultAccount.id,
      description: defaultAccount.id,
    });
  }

  const getPolicy = () => {
    let args = {
      userId: props.userId,
      groupIds: props.groupIds,
    };
    fetchPolicy(args)
  };

  function publishEvent() {
    const subscription = API.graphql(graphqlOperation(onPublishPolicy)).subscribe({
      next: (result) => {
        const policy = result.value.data.onPublishPolicy.policy;
        if (policy?.length > 0) {
          setItem(policy);
          setAccounts(concatenateAccounts(policy));
          applyDefaults(policy);
        }
        setAccountStatus("finished");
        setPermissionStatus("finished");
        subscription.unsubscribe();
      },
      error: (error) => {
        console.warn(error);
        subscription.unsubscribe();
      }
    });
    
    // Return the subscription to allow external cleanup if needed
    return subscription;
  }

  function getMentionOptions() {
    fetchUsers()
      .then((users) => {
        if (!users) {
          setMentionStatus("error");
          return;
        }
        const people = users
          .map((user) => user.UserName)
          .filter((name) => name && !MENTION_EXCLUDED_USERS.includes(name))
          .map((name) => ({ name, token: `@[user:${name}]`, kind: "メンバー" }));
        const groups = MENTION_GROUPS.map((group) => ({ ...group, kind: "ユーザーグループ" }));
        setMentionCandidates([...groups, ...people]);
        setMentionStatus("ready");
      })
      .catch(() => setMentionStatus("error"));
  }

  // 実際の <textarea> を掴む。Cloudscape の Textarea はカーソル位置を公開していない
  function justificationInput() {
    return justificationRef.current
      ? justificationRef.current.querySelector("textarea")
      : null;
  }

  // 前方一致を先に出す。部分一致だけの候補が上に来ると探しにくい
  function mentionMatches() {
    const query = mentionQuery.toLowerCase();
    return mentionCandidates
      .filter((item) => item.name.toLowerCase().includes(query))
      .sort((a, b) => {
        const aPrefix = a.name.toLowerCase().startsWith(query) ? 0 : 1;
        const bPrefix = b.name.toLowerCase().startsWith(query) ? 0 : 1;
        return aPrefix - bPrefix || a.name.localeCompare(b.name);
      });
  }

  // カーソルの直前に「@なんとか」があればメニューを開く。入力時とカーソル移動時の両方から呼ぶ
  function syncMentionMenu(value, caret) {
    const typing = MENTION_TYPING.exec(value.slice(0, caret));
    if (!typing) {
      setMentionStart(-1);
      dismissedAt.current = -1;
      return;
    }
    const start = caret - typing[2].length - 1;
    if (start === dismissedAt.current) {
      return; // Esc で閉じた @ は、そのままでは開き直さない
    }
    if (start !== mentionStart || typing[2] !== mentionQuery) {
      setMentionIndex(0);
    }
    setMentionStart(start);
    setMentionQuery(typing[2]);
  }

  function handleJustificationChange(value) {
    setJustificationError();
    setJustification(value);
    const input = justificationInput();
    syncMentionMenu(value, input ? input.selectionStart : value.length);
  }

  function closeMentionMenu(dismissed) {
    if (dismissed) {
      dismissedAt.current = mentionStart;
    }
    setMentionStart(-1);
  }

  function insertMention(candidate) {
    if (!candidate || mentionStart < 0) {
      return;
    }
    const input = justificationInput();
    const caret = input ? input.selectionStart : justification.length;
    const before = justification.slice(0, mentionStart);
    const after = justification.slice(caret);
    const spacer = after.startsWith(" ") ? "" : " ";
    const cursor = before.length + candidate.token.length + spacer.length;

    setJustification(`${before}${candidate.token}${spacer}${after}`);
    setJustificationError();
    setMentionStart(-1);
    setMentionQuery("");
    dismissedAt.current = -1;
    moveCaret(cursor);
  }

  // 「メンションを追加する」から開く。**本文には何も書かない**
  // （キャンセルされたときに @ が残ると、監査に残る申請理由が汚れるため）
  function openMentionMenu() {
    const input = justificationInput();
    const caret = input ? input.selectionStart : justification.length;
    const before = justification.slice(0, caret);
    const separator = before && !/\s$/.test(before) ? " " : "";
    if (separator) {
      setJustification(`${before} ${justification.slice(caret)}`);
    }
    setJustificationError();
    setMentionStart(caret + separator.length);
    setMentionQuery("");
    setMentionIndex(0);
    dismissedAt.current = -1;
    moveCaret(caret + separator.length);
  }

  function moveCaret(position) {
    window.setTimeout(() => {
      const el = justificationInput();
      if (el) {
        el.focus();
        el.setSelectionRange(position, position);
      }
    }, 0);
  }

  // メニューが開いている間の ↑↓ / Enter / Esc。Cloudscape の onKeyDown は
  // event.preventDefault() を実際の keydown に伝えてくれる
  function handleJustificationKeyDown(event) {
    if (!mentionOpen || event.detail.keyCode === 229) {
      return; // 229 は IME の変換中。変換確定の Enter を奪わない
    }
    if (event.detail.key === "Escape") {
      closeMentionMenu(true);
      event.preventDefault();
      return;
    }
    const matches = mentionMatches();
    if (!matches.length) {
      return;
    }
    if (event.detail.key === "ArrowDown") {
      setMentionIndex((current) => (current + 1) % matches.length);
      event.preventDefault();
    } else if (event.detail.key === "ArrowUp") {
      setMentionIndex((current) => (current - 1 + matches.length) % matches.length);
      event.preventDefault();
    } else if (event.detail.key === "Enter") {
      insertMention(matches[Math.min(mentionIndex, matches.length - 1)]);
      event.preventDefault();
    }
    // Tab は横取りしない。フォームの一項目なので、キーボードで次へ抜けられなくなる
  }

  // カーソル移動に追随する（クリックや矢印キーでの移動は onChange では拾えない）
  useEffect(() => {
    const input = justificationInput();
    if (!input) {
      return undefined;
    }
    function resync(event) {
      if (event.type === "keyup" && !CARET_KEYS.includes(event.key)) {
        return;
      }
      syncMentionMenu(input.value, input.selectionStart);
    }
    input.addEventListener("keyup", resync);
    input.addEventListener("click", resync);
    return () => {
      input.removeEventListener("keyup", resync);
      input.removeEventListener("click", resync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justification, mentionStart, mentionQuery]);

  // 選択中の候補が隠れないようにする（候補は十数件あり、メニューはスクロールする）
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [mentionIndex, mentionStart]);

  // スクリーンリーダーに、メニューの開閉と選択中の候補を伝える
  useEffect(() => {
    const input = justificationInput();
    if (!input) {
      return;
    }
    const attributes = ["role", "aria-expanded", "aria-controls", "aria-autocomplete", "aria-activedescendant"];
    if (!mentionOpen) {
      attributes.forEach((name) => input.removeAttribute(name));
      return;
    }
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-expanded", "true");
    input.setAttribute("aria-controls", "mention-listbox");
    input.setAttribute("aria-autocomplete", "list");
    const matches = mentionMatches();
    if (matches.length) {
      input.setAttribute(
        "aria-activedescendant",
        `mention-option-${Math.min(mentionIndex, matches.length - 1)}`
      );
    } else {
      input.removeAttribute("aria-activedescendant");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionStart, mentionQuery, mentionIndex, mentionCandidates]);

  function getSettings() {
    getSetting("settings").then((data) => {
      if (data !== null) {
        setMaxDuration(parseInt(data.duration));
        setTicketRequired(data.ticketNo);
        setApprovalRequired(data.approval);
      }
    });
  }

  function getMgmtPs() {
    getMgmtAccountPs().then((data) => {
      setMgmtPs(data);
    });
  }

  useEffect(() => {
    setEmail(props.user);
    getSettings();
    getMentionOptions();
    // getEligibility();
    getPolicy();
    props.addNotification([]);
    getMgmtPs();
    setTime(moment().format());
    publishEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendRequest() {
    const data = {
      accountId: account.value,
      accountName: account.label,
      role: role.label,
      roleId: role.value,
      duration: duration,
      startTime: time,
      justification: justification,
      ticketNo: ticketNo,
    };
    
    // Validate request before creating
    try {
      const validation = await validateRequest(
        account.value, 
        role.value, 
        props.userId, 
        props.groupIds
      );
      
      if (!validation.valid) {
        props.addNotification([
          {
            type: "error",
            content: `Access request denied: ${validation.reason}`,
            dismissible: true,
            onDismiss: () => props.addNotification([]),
          },
        ]);
        setSubmitLoading(false);
        return;
      }
    } catch (err) {
      props.addNotification([
        {
          type: "error",
          content: "申請の検証に失敗しました。もう一度お試しください",
          dismissible: true,
          onDismiss: () => props.addNotification([]),
        },
      ]);
      setSubmitLoading(false);
      return;
    }
    
    requestTeam(data).then(() => {
      setSubmitLoading(false);
      props.addNotification([
        {
          type: "success",
          content: "申請を作成しました",
          dismissible: true,
          onDismiss: () => props.addNotification([]),
        },
      ]);
      history.push("/requests/view");
      props.setActiveHref("/requests/view");
    });
  }

  function handleCancel() {
    history.push("/");
    props.setActiveHref("/");
    props.addNotification([]);
  }

  function sendError() {
    props.addNotification([
      {
        type: "error",
        content: `No approver for Account - ${account.label}`,
        dismissible: true,
        onDismiss: () => props.addNotification([]),
      },
    ]);
    setSubmitLoading(false);
  }

  async function validate() {
    let error = false;
    if (
      !duration ||
      isNaN(duration) ||
      Number(duration) > Number(maxDuration) ||
      Number(duration) < 1
    ) {
      setDurationError(`Enter number between 1-${maxDuration}`);
      error = true;
    }
    if (role.length < 1) {
      setRoleError("権限を選択してください");
      error = true;
    }
    if (
      params.DeploymentType == "delegated" &&
      role &&
      mgmtPs.permissions.includes(role.value)
    ) {
      setRoleError(
        "この権限は管理アカウントに割り当てられているため申請できません"
      );
      error = true;
    }
    if (!account.label) {
      setAccountError("アカウントを選択してください");
      error = true;
    }
    if (!time) {
      setTimeError("開始日時を選択してください");
      error = true;
    }
    // 先頭が @ でも弾かれないよう、文字か数字がどこかにあることだけを見る
    // （メンションから書き始められるようにするため。サーバ側の検証も同じ条件に合わせてある）
    if (!justification || !/[\p{L}\p{N}]/u.test(justification)) {
      setJustificationError("申請理由を入力してください");
      error = true;
    }
    if ((!ticketNo && ticketRequired) || !/^[a-zA-Z0-9]+$/.test(ticketNo[0])) {
      setTicketError("チケットの URL を入力してください");
      error = true;
    }
    return error;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitLoading(true);
    const isValid = await validate();
    if (!isValid) {
      const shouldSendRequest =
        !approvalRequired ||
        (await checkApprovalAndApproverGroups(account.value, role.value));
      shouldSendRequest ? sendRequest() : sendError();
    } else {
      setSubmitLoading(false);
    }
  }

  async function checkApprovalNotRequired(account, role) {
    let approvalNotRequired = false;
    for (const eligibility of item) {
      for (const acct of eligibility.accounts) {
        if (acct.id === account) {
          for (const perm of eligibility.permissions) {
            if (perm.id === role) {
              if (!eligibility.approvalRequired) {
                approvalNotRequired = true;
              }
            }
          }
        }
      }
    }
    return approvalNotRequired;
  }

  function checkGroupMembership(groupIds, groupsIds) {
    for (const groupId of groupIds) {
      if (groupsIds.includes(groupId)) {
        return true;
      }
    }
    return false;
  }
  async function checkApprovalAndApproverGroups(account, role) {
    if (await checkApprovalNotRequired(account, role)) {
      return true;
    }
    const account_approvers = await fetchApprovers(account, "Account");
    if (account_approvers) {
      const data = await getGroupMemberships(account_approvers.groupIds);
      const requesterIsApprover = checkGroupMembership(
        props.groupIds,
        account_approvers.groupIds
      );
      // If the requester is also an approver, then we need at least 2 approvers to exist (i.e. at
      // least one person who didn't make the request). Otherwise we only need a single approver to exist.
      const approverGroupMembersRequired = requesterIsApprover ? 2 : 1;

      if (data.members.length >= approverGroupMembersRequired) {
        return true;
      }
    }
    const ou = await fetchOU(account);
    const ou_approvers = await fetchApprovers(ou.Id, "OU");
    if (ou_approvers) {
      const data = await getGroupMemberships(ou_approvers.groupIds);
      const requesterIsApprover = checkGroupMembership(
        props.groupIds,
        ou_approvers.groupIds
      );
      // If the requester is also an approver, then we need at least 2 approvers to exist (i.e. at
      // least one person who didn't make the request). Otherwise we only need a single approver to exist.
      const approverGroupMembersRequired = requesterIsApprover ? 2 : 1;

      if (data.members.length >= approverGroupMembersRequired) {
        return true;
      }
    }
    return false;
  }

  return (
    <div className="container">
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              onClick={handleSubmit}
              className="buttons"
              loading={submitLoading}
            >
              Submit
            </Button>
          </SpaceBetween>
        }
      >
        <Container
          header={
            <Header
              variant="h2"
              description="一時的な権限昇格を申請する"
            >
              Elevated access request
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            <FormField
              label="Email"
              stretch
              description="申請するユーザー（ログイン中のアカウント）"
            >
              <Input value={email} type="email" />
            </FormField>
            <FormField
              label="Account"
              stretch
              description={`権限昇格する対象の AWS アカウント。既定は ${DEFAULT_ACCOUNT_NAME}`}
              errorText={accountError}
            >
              <Select
                statusType={accountStatus}
                placeholder="Select an account"
                loadingText="Loading accounts"
                filteringType="auto"
                empty="No eligible accounts found"
                options={accounts.map((account) => ({
                  label: account.name,
                  value: account.id,
                  description: account.id,
                }))}
                selectedOption={account}
                onChange={(event) => {
                  setAccountError();
                  selectAccount(item, event.detail.selectedOption);
                }}
                selectedAriaLabel="selected"
              />
            </FormField>
            <FormField
              label="Role"
              stretch
              description="申請する権限。自分に許可されているものだけが表示される"
              errorText={roleError}
            >
              <Select
                statusType={permissionStatus}
                placeholder="Select a role"
                loadingText="Loading permissions"
                filteringType="auto"
                empty="No eligible permissions found"
                options={permissions.map((permission) => ({
                  label: permission.name,
                  value: permission.id,
                }))}
                selectedOption={role}
                onChange={(event) => {
                  setRoleError();
                  setRole(event.detail.selectedOption);
                }}
                selectedAriaLabel="selected"
              />
            </FormField>
            <FormField
              label="Start time"
              stretch
              description="権限昇格を開始する日時"
              errorText={timeError}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                defaultValue={moment()}
                // disabledDate={disabledDate}
                onChange={(event) => {
                  setTimeError();
                  if (event) {
                    setTime(event._d);
                    console.log(event._d);
                  }
                }}
              />
            </FormField>
            <FormField
              label="Duration"
              stretch
              description={`権限昇格させる時間数。この時間が過ぎると権限は自動で失効する。既定は ${DEFAULT_DURATION_HOURS} 時間。${LONG_DURATION_HOURS} 時間を超える申請は、承認者が作業内容と時間の妥当性を確認する`}
              errorText={durationError}
              placeholder={`1〜${maxDuration} の数字を入力`}
            >
              <Input
                value={duration}
                onChange={(event) => {
                  setDurationError();
                  Number(event.detail.value) > Number(maxDuration)
                    ? setDurationError(
                        `1〜${maxDuration} の数字を入力してください`
                      )
                    : setDuration(event.detail.value);
                }}
                type="number"
              />
            </FormField>
            <FormField
              label="Ticket no"
              stretch
              description="関連するチケット（Notion / JIRA / Slack / etc...）の URL"
              errorText={ticketError}
            >
              <Input
                value={ticketNo}
                onChange={(event) => {
                  setTicketError();
                  setTicketNo(event.detail.value);
                }}
              />
            </FormField>
            <FormField
              label="Justification"
              stretch
              description="目的と作業内容を書く。監査で作業記録と突き合わせる材料になる"
              constraintText="@ を入力すると、この申請を見てほしい人やグループを指定できます（Slack で通知されます）"
              errorText={justificationError}
            >
              <div className="mention-anchor" ref={justificationRef}>
                {mentionOpen && (
                  <div
                    className="mention-menu"
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    {mentionMatches().length === 0 ? (
                      <div className="mention-empty" role="status">
                        {mentionStatus === "loading"
                          ? "候補を読み込んでいます…"
                          : mentionStatus === "error"
                          ? "候補を取得できませんでした"
                          : "一致する候補がありません"}
                      </div>
                    ) : (
                      <div id="mention-listbox" role="listbox" aria-label="メンションの候補">
                        {mentionMatches().map((candidate, index) => (
                          <div
                            key={candidate.token}
                            id={`mention-option-${index}`}
                            ref={index === mentionIndex ? activeItemRef : null}
                            role="option"
                            aria-selected={index === mentionIndex}
                            className={
                              index === mentionIndex
                                ? "mention-item mention-item-active"
                                : "mention-item"
                            }
                            onMouseDown={(event) => {
                              event.preventDefault();
                              insertMention(candidate);
                            }}
                            onMouseEnter={() => setMentionIndex(index)}
                          >
                            <span className="mention-name">@{candidate.name}</span>
                            <span className="mention-kind">{candidate.kind}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Textarea
                  onChange={({ detail }) => handleJustificationChange(detail.value)}
                  onKeyDown={handleJustificationKeyDown}
                  onBlur={() => closeMentionMenu(false)}
                  value={justification}
                  ariaRequired
                  placeholder="例: 受注データの不整合の調査と補正（DEV-1234）"
                />
              </div>
              <Box padding={{ top: "xxs" }}>
                <Button variant="link" iconName="user-profile" onClick={openMentionMenu}>
                  @ でメンションを挿入
                </Button>
              </Box>
            </FormField>
          </SpaceBetween>
        </Container>
      </Form>
    </div>
  );
}

export default Request;
